import React, { useLayoutEffect } from 'react';
import { Helmet } from 'react-helmet-async';

// index.html tags (for non-JS link scrapers) are dropped once page-specific tags render.
const StaticHeadCleanup = () => {
  useLayoutEffect(() => {
    document.head.querySelectorAll('[data-rh="true"]').forEach((n) => n.remove());
  }, []);
  return null;
};

const SITE_URL = 'https://scheduleguru.ws';
const DEFAULT_OG = `${SITE_URL}/og-image.png`;
const DEFAULT_TITLE = 'Schedule Guru | All-in-One Sports League Management Software';

/**
 * Reusable SEO/meta-tag component for every public-facing route.
 *
 * Uses react-helmet-async's prop API (more robust than JSX children, which
 * trips on whitespace nodes and conditional false-branches inside <title>).
 */
export default function SEOHead({
  title,
  description,
  ogTitle,
  ogDescription,
  path = '/',
  image = DEFAULT_OG,
  type = 'website',
  article,
  noindex = false,
  jsonLd,
}) {
  const fullTitle = title && title.includes('Schedule Guru')
    ? title
    : (title ? `${title} | Schedule Guru` : DEFAULT_TITLE);
  const canonical = `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const absoluteImage = image && image.startsWith('http') ? image : `${SITE_URL}${image || ''}`;
  const shareTitle = ogTitle || fullTitle;
  const shareDescription = ogDescription || description || '';

  const ldArr = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  const metaTags = [
    { name: 'description', content: description || '' },
    { property: 'og:title', content: shareTitle },
    { property: 'og:description', content: shareDescription },
    { property: 'og:url', content: canonical },
    { property: 'og:type', content: type },
    { property: 'og:image', content: absoluteImage },
    { property: 'og:image:alt', content: shareTitle },
    { property: 'og:site_name', content: 'Schedule Guru' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: shareTitle },
    { name: 'twitter:description', content: shareDescription },
    { name: 'twitter:image', content: absoluteImage },
  ];

  if (absoluteImage === DEFAULT_OG) {
    metaTags.push({ property: 'og:image:width', content: '1200' }, { property: 'og:image:height', content: '630' });
  }

  if (noindex) metaTags.push({ name: 'robots', content: 'noindex,nofollow' });

  if (type === 'article' && article) {
    if (article.publishedAt) metaTags.push({ property: 'article:published_time', content: article.publishedAt });
    if (article.modifiedAt) metaTags.push({ property: 'article:modified_time', content: article.modifiedAt });
    if (article.author) metaTags.push({ property: 'article:author', content: article.author });
    if (Array.isArray(article.tags)) {
      article.tags.forEach(t => metaTags.push({ property: 'article:tag', content: t }));
    }
  }

  return (
    <>
    <StaticHeadCleanup />
    <Helmet
      title={fullTitle}
      meta={metaTags}
      link={[{ rel: 'canonical', href: canonical }]}
      script={ldArr.map(blob => ({
        type: 'application/ld+json',
        innerHTML: JSON.stringify(blob),
      }))}
    />
    </>
  );
}

export { SITE_URL };
